import React from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { ConfigurationSettingsSkeleton } from "./MeasureGridSkeleton";

/** Keep in sync with Configuration.tsx primary nav. */
const PRIMARY_TABS = [
  { value: 'qpp-reports', label: 'QPP Reports', short: 'QPP' },
  { value: 'practice', label: 'Practice', short: 'Practice' },
  { value: 'mips-categories', label: 'Manage MIPS Categories', short: 'Manage Categories' },
  { value: 'mvps', label: 'Manage MVPs', short: 'MVPs' },
] as const;

const CATEGORY_SUBTABS = [
  { value: 'quality', label: 'Quality', short: 'Quality' },
  { value: 'pi', label: 'Promoting Interoperability', short: 'PI' },
  { value: 'cost', label: 'Cost', short: 'Cost' },
  { value: 'ia', label: 'Improvement Activities', short: 'IA' },
] as const;

const PRACTICE_SUBTABS = [
  { value: 'practice', label: 'Profile' },
  { value: 'preferences', label: 'Preferences' },
] as const;

function resolvePrimaryTab(activeTab: string): string {
  if (activeTab === 'preferences' || activeTab === 'practice') return 'practice';
  if (activeTab === 'quality' || activeTab === 'pi' || activeTab === 'cost' || activeTab === 'ia') {
    return 'mips-categories';
  }
  return activeTab;
}

function SubtabSkeleton({
  items,
  activeValue,
}: {
  items: readonly { value: string; label: string; short?: string }[];
  activeValue: string;
}) {
  return (
    <div className="inline-flex h-auto w-full justify-start gap-0 border-b border-slate-200">
      {items.map(({ value, label, short }) => {
        const active = activeValue === value;
        return (
          <div
            key={value}
            className={`rounded-none border-b-2 bg-transparent px-4 py-2.5 text-sm font-medium ${
              active
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500'
            }`}
          >
            {short ? (
              <>
                <span className="hidden 2xl:inline">{label}</span>
                <span className="2xl:hidden">{short}</span>
              </>
            ) : (
              label
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ConfigurationFullPageSkeleton({ activeTab = 'practice' }: { activeTab?: string }) {
  const primaryTab = resolvePrimaryTab(activeTab);
  const showCategorySubtabs = primaryTab === 'mips-categories';
  const showPracticeSubtabs = primaryTab === 'practice';

  return (
    <div>
      <Skeleton className="mb-4 h-9 w-48" />

      <div className="space-y-4">
        <div className="grid w-full grid-cols-4 gap-1 2xl:gap-2">
          {PRIMARY_TABS.map(({ value, label, short }) => (
            <div
              key={value}
              className={`px-1.5 2xl:px-4 py-3 text-xs 2xl:text-sm font-medium rounded-lg transition-all flex items-center justify-center text-center ${
                primaryTab === value
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-slate-100 text-slate-700'
              }`}
            >
              <span className="hidden 2xl:inline">{label}</span>
              <span className="2xl:hidden">{short}</span>
            </div>
          ))}
        </div>

        {showCategorySubtabs && (
          <SubtabSkeleton items={CATEGORY_SUBTABS} activeValue={activeTab} />
        )}
        {showPracticeSubtabs && (
          <SubtabSkeleton
            items={PRACTICE_SUBTABS}
            activeValue={activeTab === 'preferences' ? 'preferences' : 'practice'}
          />
        )}

        <div className="space-y-4">
          {(activeTab === 'practice' || activeTab === 'qpp-reports') && <ConfigurationSettingsSkeleton />}

          {activeTab === 'preferences' && (
            <div className="space-y-4 pt-4">
              <div className="rounded-lg border p-6">
                <Skeleton className="h-6 w-40 mb-2" />
                <Skeleton className="h-4 w-64 mb-6" />
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                      <div>
                        <Skeleton className="h-4 w-32 mb-1" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                      <Skeleton className="h-6 w-12 rounded-full" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'quality' && (
            <div className="space-y-4 pt-4">
              <div className="rounded-lg border p-6">
                <Skeleton className="h-6 w-24 mb-2" />
                <Skeleton className="h-4 w-48 mb-4" />
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-lg border p-4">
                    <Skeleton className="h-5 w-20 mb-2" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <div className="flex gap-2 mb-3">
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-full mb-1" />
                    <Skeleton className="h-3 w-4/5 mb-4" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(activeTab === 'pi' || activeTab === 'ia' || activeTab === 'cost') && (
            <div className="space-y-4 pt-4">
              <div className="rounded-lg border p-6">
                <Skeleton className="h-6 w-48 mb-4" />
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="p-4 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Skeleton className="h-5 w-32 mb-2" />
                          <Skeleton className="h-4 w-full max-w-md mb-1" />
                          <Skeleton className="h-4 w-3/4 max-w-sm" />
                        </div>
                        <Skeleton className="h-10 w-24 rounded-md" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mvps' && (
            <div className="space-y-4">
              <div className="rounded-lg border p-6">
                <div className="flex flex-col lg:flex-row gap-6 min-h-[128px]">
                  <div className="space-y-2 w-full lg:w-[220px]">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-full max-w-md" />
                    <Skeleton className="h-4 w-2/3 max-w-sm" />
                  </div>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-lg border p-4 space-y-3">
                    <div className="flex justify-between gap-2">
                      <Skeleton className="h-5 w-2/3" />
                      <Skeleton className="h-5 w-14 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-8 w-28" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
